import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { IoMdNotificationsOutline } from "react-icons/io";
import {
  Crown,
  User,
  Calendar,
  CheckCircle,
  Mail,
  UserPlus,
} from "lucide-react";
import {
  fetchNotifications,
  markNotificationAsRead,
  clearAllNotifications,
} from "../../redux/slices/notificationsSlice";
import axios from "axios";
import Swal from "sweetalert2";
const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useDispatch();
  const { items: notifications, loading } = useSelector(
    (state) => state.notifications
  );

  useEffect(() => {
    // Cargar notificaciones al montar el componente
    dispatch(fetchNotifications());

    // Configurar un intervalo para verificar nuevas notificaciones cada 5 minutos
    const interval = setInterval(() => {
      dispatch(fetchNotifications());
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [dispatch]);

  // Filtrar para mostrar solo notificaciones no leídas
  const unreadNotifications = notifications.filter(
    (notification) => !notification.read
  );
  const unreadCount = unreadNotifications.length;

  const handleToggleNotifications = () => {
    setIsOpen(!isOpen);
  };

  const handleMarkAsRead = (notificationId) => {
    dispatch(markNotificationAsRead(notificationId));
    // Cerrar el panel de notificaciones si ya no hay notificaciones no leídas
    if (unreadCount === 1) {
      setTimeout(() => setIsOpen(false), 300); // Pequeño retraso para mejor experiencia de usuario
    }
  };

  const handleClearAll = () => {
    dispatch(clearAllNotifications());
    setIsOpen(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="relative">
      <button
        onClick={handleToggleNotifications}
        className="text-white transition-transform duration-700 hover:text-gray-400 hover:scale-110 relative"
      >
        <IoMdNotificationsOutline className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-[#242424]/90 rounded shadow-lg z-50 max-h-96 overflow-y-auto">
          <div className="p-3 border-b border-gray-700 flex justify-between items-center">
            <h3 className="text-white font-semibold">Notificaciones</h3>
            {unreadNotifications.length > 0 && (
              <button
                onClick={handleClearAll}
                className="text-xs text-gray-400 hover:text-white transition-colors"
              >
                Limpiar todo
              </button>
            )}
          </div>

          {loading ? (
            <div className="p-4 text-center text-gray-300">Cargando...</div>
          ) : unreadNotifications.length === 0 ? (
            <div className="p-4 text-center text-gray-300">
              No tienes notificaciones
            </div>
          ) : (
            <div>
              {unreadNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className="p-3 border-b border-gray-700 hover:bg-gray-800 transition-colors cursor-pointer"
                  onClick={() => handleMarkAsRead(notification.id)}
                >
                  <div className="flex items-start">
                    <div
                      className={`w-2 h-2 rounded-full mt-1.5 mr-2 ${
                        notification.type === "success"
                          ? "bg-green-500"
                          : notification.type === "error"
                          ? "bg-red-500"
                          : notification.type === "subscription"
                          ? "bg-purple-500"
                          : "bg-blue-500"
                      }`}
                    ></div>
                    <div className="flex-1">
                      {notification.isNewUser ? (
                        <div>
                          <div className="flex items-center mb-1">
                            <UserPlus className="w-3 h-3 mr-1 text-green-400" />
                            <p className="text-sm text-white font-medium">
                              Nuevo usuario registrado
                            </p>
                          </div>
                          <div className="flex items-center mb-1">
                            <User className="w-3 h-3 mr-1 text-blue-400" />
                            <p className="text-sm text-white">
                              {notification.userName}
                            </p>
                          </div>
                          {notification.userEmail && (
                            <div className="flex items-center mb-1">
                              <Mail className="w-3 h-3 mr-1 text-gray-400" />
                              <p className="text-xs text-gray-300">
                                {notification.userEmail}
                              </p>
                            </div>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            {formatDate(notification.date)}
                          </p>
                        </div>
                      ) : notification.isSubscriptionRequest ? (
                        <div>
                          <div className="flex items-center mb-1">
                            <User className="w-3 h-3 mr-1 text-blue-400" />
                            <p className="text-sm text-white font-medium">
                              {notification.userName}
                            </p>
                          </div>
                          <div className="flex items-center mb-1">
                            <Crown className="w-3 h-3 mr-1 text-yellow-400" />
                            <p className="text-sm text-white">
                              Solicitó el plan:{" "}
                              <span className="text-yellow-400">
                                {notification.planName}
                              </span>
                            </p>
                          </div>
                          <div className="flex justify-between items-center mt-2">
                            <p className="text-xs text-gray-400">
                              {formatDate(notification.date)}
                            </p>
                            <div className="flex space-x-2">
                              <button
                                className="text-xs bg-green-600/30 text-green-400 px-2 py-0.5 rounded border border-green-500/20 hover:bg-green-600/50 transition-colors"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    const token = localStorage.getItem("token");
                                    if (!token) {
                                      alert(
                                        "No se pudo aprobar la suscripción: No hay sesión activa"
                                      );
                                      return;
                                    }

                                    // Llamar al endpoint para aprobar la suscripción
                                    const response = await axios.put(
                                      `${
                                        process.env.REACT_APP_API_URL ||
                                        "http://localhost:4005"
                                      }/api/v1/userSubscriptions/approve/${
                                        notification.subscriptionId
                                      }`,
                                      {},
                                      {
                                        headers: {
                                          Authorization: `Bearer ${token}`,
                                          "Content-Type": "application/json",
                                        },
                                      }
                                    );

                                    if (response.status === 200) {
                                      // Marcar la notificación como leída
                                      dispatch(
                                        markNotificationAsRead(notification.id)
                                      );
                                      // Actualizar las notificaciones
                                      setTimeout(() => {
                                        dispatch(fetchNotifications());
                                      }, 500); // Pequeño retraso para asegurar que la actualización del backend se complete

                                      await Swal.fire({
                                        title: "¡Éxito!",
                                        text: `Suscripción de ${notification.userName} al plan ${notification.planName} aprobada correctamente`,
                                        icon: "success",
                                        confirmButtonText: "Aceptar",
                                        timer: 2500,
                                        timerProgressBar: true,
                                        showConfirmButton: false,
                                      });
                                      setIsOpen(false);
                                    }
                                  } catch (error) {
                                    console.error(
                                      "Error al aprobar la suscripción:",
                                      error
                                    );
                                    alert(
                                      `Error al aprobar la suscripción: ${
                                        error.response?.data?.message ||
                                        error.message
                                      }`
                                    );
                                  }
                                }}
                              >
                                Aprobar
                              </button>
                              <button
                                className="text-xs bg-red-600/30 text-red-400 px-2 py-0.5 rounded border border-red-500/20 hover:bg-red-600/50 transition-colors"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    const token = localStorage.getItem("token");
                                    if (!token) {
                                      alert(
                                        "No se pudo rechazar la suscripción: No hay sesión activa"
                                      );
                                      return;
                                    }

                                    // Llamar al endpoint para rechazar la suscripción
                                    const response = await axios.put(
                                      `${
                                        process.env.REACT_APP_API_URL ||
                                        "http://localhost:4005"
                                      }/api/v1/userSubscriptions/reject/${
                                        notification.subscriptionId
                                      }`,
                                      {},
                                      {
                                        headers: {
                                          Authorization: `Bearer ${token}`,
                                          "Content-Type": "application/json",
                                        },
                                      }
                                    );

                                    if (response.status === 200) {
                                      // Marcar la notificación como leída
                                      dispatch(
                                        markNotificationAsRead(notification.id)
                                      );
                                      // Actualizar las notificaciones
                                      setTimeout(() => {
                                        dispatch(fetchNotifications());
                                      }, 500); // Pequeño retraso para asegurar que la actualización del backend se complete

                                      await Swal.fire({
                                        title: "¡Rechazado!",
                                        text: `Suscripción de ${notification.userName} al plan ${notification.planName} rechazada correctamente.`,
                                        icon: "error", 
                                        confirmButtonText: "Aceptar",
                                        timer: 2500,
                                        timerProgressBar: true,
                                        showConfirmButton: false,
                                      });

                                      setIsOpen(false);
                                    }
                                  } catch (error) {
                                    console.error(
                                      "Error al rechazar la suscripción:",
                                      error
                                    );
                                    alert(
                                      `Error al rechazar la suscripción: ${
                                        error.response?.data?.message ||
                                        error.message
                                      }`
                                    );
                                  }
                                }}
                              >
                                Rechazar
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="text-sm text-white mb-1">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-400">
                            {formatDate(notification.date)}
                          </p>
                        </>
                      )}
                    </div>
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
